import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Lock, 
  Loader2, 
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import PortalFooter from '../components/PortalFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PasswordResetPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  
  // Get params from URL
  const emailParam = searchParams.get('email') || '';
  const tokenParam = searchParams.get('token') || '';
  
  // Mode: 'token' (forgot password) or 'password' (first login)
  const [mode, setMode] = useState(tokenParam ? 'token' : 'password');
  const [email, setEmail] = useState(emailParam);
  
  // State
  const [loading, setLoading] = useState(false);
  const [verifyingToken, setVerifyingToken] = useState(!!tokenParam);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  
  // Form data
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Password visibility
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  // Password requirements
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  // Verify token on mount if present
  useEffect(() => {
    if (tokenParam) {
      verifyToken();
    }
  }, [tokenParam]);

  // Check password requirements
  useEffect(() => {
    setRequirements({
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(newPassword)
    });
  }, [newPassword]);

  const allRequirementsMet = Object.values(requirements).every(Boolean);

  const verifyToken = async () => {
    try {
      setVerifyingToken(true);
      const response = await fetch(`${API_URL}/api/auth/verify-reset-token/${tokenParam}`);
      const data = await response.json();
      
      if (data.valid) {
        setTokenValid(true);
        setEmail(data.email);
        setMode('token');
      } else {
        setError(data.error || 'Invalid or expired reset link');
        setTokenValid(false);
      }
    } catch (err) {
      setError('Failed to verify reset link');
      setTokenValid(false);
    } finally {
      setVerifyingToken(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate
    if (mode === 'password' && !currentPassword) {
      setError('Please enter your current/temporary password');
      return;
    }
    
    if (!allRequirementsMet) {
      setError('Password does not meet all requirements');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (mode === 'password' && newPassword === currentPassword) {
      setError('New password must be different from current password');
      return;
    }
    
    try {
      setLoading(true);
      
      let response;
      
      if (mode === 'token') {
        // Reset with token (forgot password flow)
        response = await fetch(`${API_URL}/api/auth/reset-password-with-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: tokenParam,
            new_password: newPassword
          })
        });
      } else {
        // Reset with current password (first login flow)
        response = await fetch(`${API_URL}/api/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            current_password: currentPassword,
            new_password: newPassword
          })
        });
      }
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }
      
      // Store new token using AuthContext
      if (data.token) {
        login(data.token, data.policies || [], data.email || email, data.policies?.[0]);
      }
      
      setSuccess(true);
      
      // Redirect to dashboard after success
      setTimeout(() => {
        window.location.href = `${window.location.origin}/portal`;
      }, 2000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const RequirementItem = ({ met, text }) => (
    <div className={`flex items-center gap-2 text-sm ${met ? 'text-green-600' : 'text-slate-500'}`}>
      {met ? (
        <CheckCircle className="h-4 w-4" />
      ) : (
        <div className="h-4 w-4 rounded-full border-2 border-slate-300" />
      )}
      <span>{text}</span>
    </div>
  );

  // Loading state while verifying token
  if (verifyingToken) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex flex-col">
        <header className="bg-white border-b border-slate-200/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center h-16">
              <a href="https://losspay.com" className="flex-shrink-0">
                <img src="https://images.losspay.com/losspay/losspay-logo.png" alt="LossPay" className="h-8 md:h-9" />
              </a>
            </div>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-[#3B5BDB] mx-auto mb-4" />
            <p className="text-slate-600 font-medium">Verifying your reset link...</p>
          </div>
        </main>
        <PortalFooter />
      </div>
    );
  }

  // Invalid token error state
  if (mode === 'token' && !tokenValid && !verifyingToken) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex flex-col">
        <header className="bg-white border-b border-slate-200/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center h-16">
              <a href="https://losspay.com" className="flex-shrink-0">
                <img src="https://images.losspay.com/losspay/losspay-logo.png" alt="LossPay" className="h-8 md:h-9" />
              </a>
            </div>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md shadow-xl border-0 rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Invalid Reset Link
              </h2>
              <p className="text-slate-600 mb-6">
                {error || 'This password reset link is invalid or has expired.'}
              </p>
              <Button
                onClick={() => navigate('/portal/forgot-password')}
                className="w-full h-12 bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl font-semibold"
              >
                Request New Reset Link
              </Button>
            </CardContent>
          </Card>
        </main>
        <PortalFooter />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex flex-col">
        <header className="bg-white border-b border-slate-200/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center h-16">
              <a href="https://losspay.com" className="flex-shrink-0">
                <img src="https://images.losspay.com/losspay/losspay-logo.png" alt="LossPay" className="h-8 md:h-9" />
              </a>
            </div>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md shadow-xl border-0 rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Password <span className="text-[#3B5BDB]">Updated!</span>
              </h2>
              <p className="text-slate-600 mb-6">
                Your password has been successfully changed. Redirecting to your dashboard...
              </p>
              <Loader2 className="h-6 w-6 animate-spin text-[#3B5BDB] mx-auto" />
            </CardContent>
          </Card>
        </main>
        <PortalFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8EEFF] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <a href="https://losspay.com" className="flex-shrink-0">
              <img 
                src="https://images.losspay.com/losspay/losspay-logo.png" 
                alt="LossPay" 
                className="h-8 md:h-9"
              />
            </a>
            <nav className="hidden md:flex items-center gap-8">
              <a href="https://losspay.com/coverage" className="text-slate-600 hover:text-slate-900 font-medium">Coverage</a>
              <a href="https://losspay.com/why-losspay" className="text-slate-600 hover:text-slate-900 font-medium">Why LossPay</a>
              <a href="/claims" className="text-slate-600 hover:text-slate-900 font-medium">Claims</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-start md:items-center justify-center px-4 py-6 md:py-12">
        <div className="w-full max-w-md">
          
          {/* Badge - smaller on mobile */}
          <div className="text-center mb-4 md:mb-6">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/80 rounded-full text-xs md:text-sm font-medium text-[#3B5BDB]">
              <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[#3B5BDB] rounded-full"></span>
              {mode === 'token' ? 'Password Reset' : 'Set New Password'}
            </span>
          </div>

          <Card className="shadow-xl border-0 rounded-2xl">
            <CardContent className="p-5 md:p-8">
              {/* Compact header on mobile */}
              <div className="text-center mb-4 md:mb-8">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#E8EEFF] rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <Lock className="h-6 w-6 md:h-8 md:w-8 text-[#3B5BDB]" />
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900 mb-1 md:mb-2">
                  {mode === 'token' ? (
                    <>Create New <span className="text-[#3B5BDB]">Password</span></>
                  ) : (
                    <>Set Your <span className="text-[#3B5BDB]">Password</span></>
                  )}
                </h1>
                <p className="text-sm md:text-base text-slate-600">
                  {mode === 'token' 
                    ? 'Enter a new password for your account.'
                    : 'Create a secure password for your account.'
                  }
                </p>
              </div>
              
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                {/* Email display - more compact on mobile */}
                <div className="p-3 md:p-4 bg-slate-50 rounded-xl">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Account</p>
                  <p className="font-semibold text-slate-900 text-sm md:text-base">{email}</p>
                </div>
                
                {/* Current/Temporary Password - only for password mode */}
                {mode === 'password' && (
                  <div className="space-y-1.5 md:space-y-2">
                    <Label htmlFor="current" className="text-slate-700 font-medium text-sm md:text-base">Temporary Password</Label>
                    <div className="relative">
                      <Input
                        id="current"
                        type={showCurrent ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter the password from your welcome email"
                        className="h-11 md:h-12 pr-12 rounded-xl border-slate-200 focus:border-[#3B5BDB] focus:ring-[#3B5BDB]/20 text-sm md:text-base"
                        data-testid="current-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrent(!showCurrent)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showCurrent ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* New Password */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="new" className="text-slate-700 font-medium text-sm md:text-base">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new"
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Create a strong password"
                      className="h-11 md:h-12 pr-12 rounded-xl border-slate-200 focus:border-[#3B5BDB] focus:ring-[#3B5BDB]/20 text-sm md:text-base"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew(!showNew)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNew ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                {/* Password Requirements - more compact on mobile */}
                <div className="p-3 md:p-4 bg-slate-50 rounded-xl space-y-1.5 md:space-y-2">
                  <p className="text-xs md:text-sm font-medium text-slate-700 mb-2 md:mb-3">Password Requirements:</p>
                  <RequirementItem met={requirements.length} text="At least 8 characters" />
                  <RequirementItem met={requirements.uppercase} text="One uppercase letter" />
                  <RequirementItem met={requirements.lowercase} text="One lowercase letter" />
                  <RequirementItem met={requirements.number} text="One number" />
                  <RequirementItem met={requirements.special} text="One special character (!@#$%...)" />
                </div>
                
                {/* Confirm Password */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="confirm" className="text-slate-700 font-medium text-sm md:text-base">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your new password"
                      className="h-11 md:h-12 pr-12 rounded-xl border-slate-200 focus:border-[#3B5BDB] focus:ring-[#3B5BDB]/20 text-sm md:text-base"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs md:text-sm text-red-600">Passwords do not match</p>
                  )}
                </div>
                
                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full h-11 md:h-12 bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl font-semibold text-sm md:text-base"
                  disabled={loading || !allRequirementsMet || newPassword !== confirmPassword}
                  data-testid="reset-password-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 md:h-5 md:w-5 animate-spin" />
                      Updating Password...
                    </>
                  ) : (
                    'Set New Password'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Security Note - smaller on mobile */}
          <p className="text-xs text-slate-500 text-center mt-4 md:mt-6 px-4">
            Your information is protected using secure encryption.
          </p>
          
        </div>
      </main>

      {/* Footer */}
      <PortalFooter />
    </div>
  );
}
